import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SubscriptionService } from '@shared-api/subscription/subscription.service';
import { WorkflowService } from '@shared-api/workflow/workflow.service';
import { AppSubscription, sortState } from '@shared-interfaces/shared-subscription';
import { Workflow, WorkflowBasic } from '@shared-interfaces/shared-workflow';
import { EMPTY, Subject, catchError, switchMap, tap, Observable } from 'rxjs';
import { PageEvent } from "@angular/material/paginator";
import { WorkflowOverviewService } from "./workflow-overview.service";

export type SubscriptionDataStateStatus =
  | 'loading'
  | 'success'
  | 'error';

interface SubscriptionDataState {
  status: SubscriptionDataStateStatus;
  subscriptionData: AppSubscription
}


@Injectable({
  providedIn: 'root'
})
export class SubscriptionOverviewService {

  private readonly DEFAULT_PAGE_SIZE: number = 20;

  private workflowOverviewService = inject(WorkflowOverviewService);
  private subscriptionService = inject(SubscriptionService);
  private workflowService = inject(WorkflowService);
  private compareDates = (a: Date, b: Date, order: 'ascend' | 'descend'): number =>
    (new Date(a).getTime() - new Date(b).getTime()) * (order === 'ascend' ? 1 : -1);

  // state
  private componentState = signal<SubscriptionDataState>({
    status: 'loading',
    subscriptionData: {
      subscriptionId: undefined,
      contentCollection: [],
    }
  });

  public sortState = signal<sortState>({
    ascend: false,
    descend: true
  })

  public filterStatusState = signal<number>(0)

  public searchTerm = signal<string>('');

  // selectors
  status = computed(() => this.componentState().status);
  subscriptionData = computed(() => this.componentState().subscriptionData)
  subscriptionId = computed(() => this.componentState().subscriptionData.subscriptionId)
  pageEvent = signal<PageEvent | undefined>(undefined)
  numContractsPerPage = computed(() => {
    return this.pageEvent()?.pageSize || this.DEFAULT_PAGE_SIZE;
  });
  lastCreatedWorkflow = signal<WorkflowBasic | undefined>(undefined);
  workflowOverview = computed(() => {
    const data = this.componentState().subscriptionData?.contentCollection || [];
    const sortOrder = this.sortState().descend ? 'descend' : 'ascend';
    const statusFilter = this.filterStatusState();
    const searchTerm = this.searchTerm();

    const filteredData = data.filter(workflow => workflow.contentCollectionType === 'contract')
      .filter(workflow => workflow.name?.toLowerCase().includes(searchTerm) || false)
      .filter(workflow => {
        if (statusFilter === 0) {
          return true;
        } else {
          return workflow.activeStep.toString().substring(0, 1) === statusFilter.toString();
        }
      });
    return filteredData.slice().sort((a, b) => this.compareDates(a.creationDate, b.creationDate, sortOrder));
  });

  workflowOverviewFiltered = computed(() => {
    const workflows = this.workflowOverview() || [];
    const pageIndex = this.pageEvent()?.pageIndex || 0;
    const pageSize = this.pageEvent()?.pageSize || this.DEFAULT_PAGE_SIZE;

    return workflows.filter((_, index) => {
      return (index >= pageIndex * pageSize) && index < ((pageIndex + 1) * pageSize);
    });
  });

  // Actions
  private error$ = new Subject<any>();

  getSubscriptionData$ = new Subject<void>();
  subscriptionData$ = this.getSubscriptionData$.pipe(
    switchMap(() =>
      this.getSubscriptionData().pipe(
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        }),
      ),
    ),
  )

  createNewWorkflow$ = new Subject<[string, string]>();
  newWorkflow$ = this.createNewWorkflow$.pipe(
    switchMap(([workflowName, workflowType]) => {
      this.updateStatus("loading");
      return this.createNewWorkflow(workflowName, workflowType).pipe(
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        }),
      )
    }),
  )

  createNewPrompt$ = new Subject<string>();
  newPrompt$ = this.createNewPrompt$.pipe(
    switchMap((workflowName) => {
      this.updateStatus("loading");
      return this.createNewPrompt(workflowName).pipe(
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        }),
      )
    }),
  )

  deleteWorkflowId$ = new Subject<string>();
  deleteWorkflow$ = this.deleteWorkflowId$.pipe(
    switchMap((workflowId: string) => {
      this.updateStatus("loading");
      return this.deleteWorkflow(workflowId).pipe(
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        }),
      )
    }),
  )

  constructor() {
    // reducers
    this.subscriptionData$.pipe(takeUntilDestroyed()).subscribe({
      next: (subscriptionData: AppSubscription) => {
        this.componentState.update((componentState) => ({
          ...componentState,
          status: 'success',
          subscriptionData: subscriptionData,
        }));
      },
    });

    this.error$.pipe(takeUntilDestroyed()).subscribe(() =>
      this.updateStatus("error")
    );

    this.newWorkflow$.pipe(takeUntilDestroyed()).subscribe({
      next: (workflowData: WorkflowBasic) => {
        this.lastCreatedWorkflow.set(workflowData);
        this.componentState.update((componentState) => ({
          ...componentState,
          status: "success",
          subscriptionData: {
            subscriptionId: componentState.subscriptionData.subscriptionId,
            contentCollection: [
              ...componentState.subscriptionData.contentCollection,
              workflowData,
            ]
          },
        }));
      },
    })

    this.newPrompt$.pipe(takeUntilDestroyed()).subscribe({
      next: (workflowData: WorkflowBasic) => {
        this.lastCreatedWorkflow.set(workflowData);
        this.componentState.update((componentState) => ({
          ...componentState,
          status: "success",
          subscriptionData: {
            subscriptionId: componentState.subscriptionData.subscriptionId,
            contentCollection: [
              ...componentState.subscriptionData.contentCollection,
              workflowData,
            ]
          },
        }));
      },
    })

    this.deleteWorkflow$.pipe(takeUntilDestroyed()).subscribe({
      next: (workflowId: string) => {
        this.componentState.update((componentState) => ({
          ...componentState,
          status: "success",
          subscriptionData: {
            subscriptionId: componentState.subscriptionData.subscriptionId,
            contentCollection: componentState.subscriptionData.contentCollection.filter(
              el => el.uid != workflowId
            )
          },
        }));
      },
    })
  }

  private getSubscriptionData() {
    return this.subscriptionService.getSubscriptionData()
  }


  private createNewWorkflow(workflowName: string, workflowType: string) {
    return this.workflowService.createNewWorkflow(workflowName, workflowType)
  }

  private createNewPrompt(workflowName: string) {
    return this.workflowService.createPromptTesting(workflowName)
  }

  public updateWorflowName(workflowId: string, name: string): Observable<any> {
    return this.workflowService.updateWorkflowName(workflowId, name)
      .pipe(tap(() => this.updateWorkflowStateName(workflowId, name)))
  }

  private updateWorkflowStateName(workflowId: string, name: string): void {
    this.componentState.update(state => {
      return {
        ...state, subscriptionData: {
          ...state.subscriptionData,
          contentCollection: state.subscriptionData.contentCollection.map(i => {
            return i.uid === workflowId ? { ...i, name } : i;
          })
        }
      }
    })

    this.workflowOverviewService.updateContractName(name);
  }

  private deleteWorkflow(workflowId: string) {
    return this.workflowService.deleteWorkflow(workflowId)
      .pipe(tap(() => {
        this.componentState.update((componentState) => ({
          ...componentState,
          status: "success",
          subscriptionData: {
            subscriptionId: componentState.subscriptionData.subscriptionId,
            contentCollection: componentState.subscriptionData.contentCollection.filter(
              el => el.uid != workflowId
            )
          },
        }));
      }))
  }

  private updateStatus(status: SubscriptionDataStateStatus) {
    this.componentState.update((componentState) => ({
      ...componentState,
      status: status,
    }));
  }

}
